'use client';

import { useState, useEffect } from 'react';
import { translateApiError, translateErrorMessage } from '@/lib/i18n/translate-api-error';
import { useRouter } from 'next/navigation';
import { apiClient } from '@/lib/api-client';
import { getAccessToken, getStoredUserJson, hasStoredUser } from '@/lib/auth-storage';

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  tenantId: string;
  role: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  apiKey: string | null;
}

export function useAuth() {
  const router = useRouter();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    apiKey: null,
  });

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const token = getAccessToken();
    const apiKey = localStorage.getItem('api_key');
    const isAuthenticated = !!token || hasStoredUser();

    if (isAuthenticated) {
      const userData = getStoredUserJson();
      let user = null;

      if (userData) {
        try {
          user = JSON.parse(userData);
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      setAuthState({
        isAuthenticated: !!token || !!user,
        user,
        isLoading: false,
        apiKey: apiKey,
      });
    } else {
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        apiKey: null,
      });
    }
  };

  const login = async (credentials: { email: string; password: string }) => {
    try {
      const response = await apiClient.login(credentials);
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(response.user));
      
      setAuthState({
        isAuthenticated: true,
        user: response.user as any, // Type assertion for compatibility
        isLoading: false,
        apiKey: null, // Frontend uses JWT, not API Key
      });

      // Check for redirect URL
      const redirectUrl = localStorage.getItem('redirectAfterLogin') || '/dashboard';
      localStorage.removeItem('redirectAfterLogin');
      
      router.push(redirectUrl);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? translateApiError(error) : translateErrorMessage(null, 'LOGIN_FAILED'),
      };
    }
  };

  const register = async (credentials: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    tenantId: string;
  }) => {
    try {
      const response = await apiClient.register(credentials);
      
      if (response.success && response.data) {
        // Store user data
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        setAuthState({
          isAuthenticated: true,
          user: response.data.user as any, // Type assertion for compatibility
          isLoading: false,
          apiKey: response.data.apiKey,
        });

        router.push('/dashboard');
        
        return { success: true };
      } else {
        return {
          success: false,
          error: translateErrorMessage(response.message, 'REGISTRATION_FAILED'),
        };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? translateApiError(error) : translateErrorMessage(null, 'REGISTRATION_FAILED'),
      };
    }
  };

  const logout = () => {
    void apiClient.logout().finally(() => {
      localStorage.removeItem('user');

      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        apiKey: null,
      });

      router.push('/login');
    });
  };

  const refreshAuth = () => {
    checkAuthStatus();
  };

  return {
    ...authState,
    login,
    register,
    logout,
    refreshAuth,
  };
}
